"use client";
import { DbClient } from "@tanstack/db";
import { QueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { ApiError } from "@softmax/observatory-client/transport";
import { shouldRetryQuery } from "./query-retry-policy";

export type BrowserDataOwnerOptions = {
  readonly viewerId: string;
  readonly elevatedPrivileges?: boolean;
  readonly onAuthorizationError?: (failure: {
    readonly client: QueryClient;
    readonly queryKey?: QueryKey | undefined;
  }) => void;
  /** The host owns this client's identity boundary and disposal. */
  readonly queryClient?: QueryClient;
};

export function useBrowserDataOwner({
  viewerId,
  elevatedPrivileges = false,
  onAuthorizationError,
  queryClient: providedClient,
}: BrowserDataOwnerOptions) {
  const authorizationErrorHandler = useRef(onAuthorizationError);
  const activeDbClient = useRef<DbClient | undefined>(undefined);
  useEffect(() => {
    authorizationErrorHandler.current = onAuthorizationError;
  }, [onAuthorizationError]);
  const scope = useMemo(() => {
    const queryClient =
      providedClient ??
      new QueryClient({
        defaultOptions: { queries: { retry: shouldRetryQuery } },
      });
    const reportAuthorizationFailure = ({
      error,
      queryKey,
    }: {
      readonly error: Error;
      readonly queryKey?: QueryKey;
    }) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      ) {
        authorizationErrorHandler.current?.({ client: queryClient, queryKey });
      }
    };
    const reportAuthorizationError = (error: Error) =>
      reportAuthorizationFailure({ error });
    const lifetime = new AbortController();
    // oxlint-disable-next-line react/refs -- DbClient stores the callback without invoking it during construction.
    const dbClient = new DbClient({
      queryClient,
      viewerId,
      elevatedPrivileges,
      reportAuthorizationError,
      lifetime,
    });
    return {
      key: JSON.stringify([viewerId, elevatedPrivileges]),
      viewerId,
      elevatedPrivileges,
      queryClient,
      dbClient,
      lifetime,
      reportAuthorizationError,
      reportAuthorizationFailure,
    };
  }, [providedClient, viewerId, elevatedPrivileges]);
  useEffect(() => {
    const {
      queryClient,
      dbClient,
      lifetime,
      reportAuthorizationError,
      reportAuthorizationFailure,
    } = scope;
    activeDbClient.current = dbClient;
    const queries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error")
        reportAuthorizationFailure({
          error: event.action.error,
          queryKey: event.query.queryKey,
        });
    });
    const mutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error")
        reportAuthorizationError(event.action.error);
    });
    return () => {
      queries();
      mutations();
      activeDbClient.current = undefined;

      // StrictMode restores the same scope before this microtask runs.
      queueMicrotask(() => {
        if (activeDbClient.current === dbClient) return;

        lifetime.abort();

        if (!providedClient) void queryClient.cancelQueries();

        void dbClient.cleanup().finally(() => {
          if (!providedClient) queryClient.clear();
        });
      });
    };
  }, [scope, providedClient]);
  return scope;
}
